// dashboardMetrics.js — BL-039 (Investor Intelligence Dashboard, Phase 1)
//
// Authenticated data-access layer for the dashboard metrics API. Same
// JWT-Bearer pattern as lib/qualifications / lib/admin: pull the live access
// token from the Supabase session and call the user-scoped, read-only
// /get-dashboard-metrics function, which enforces user_id from the verified
// token. Returns the shared { data, error } shape and never throws.
//
// The endpoint returns SIGNAL data only (ADR-044); presentation frames it.

import { supabase } from './supabaseClient';
import { API_BASE_URL } from '../services/api';

const NOT_SIGNED_IN = {
  data: null,
  error: { message: 'Not signed in. Please sign in and try again.' },
};

const NOT_CONFIGURED = {
  data: null,
  error: {
    message:
      'Supabase is not configured. Fill in frontend/.env (REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY) and restart the dev server.',
  },
};

/**
 * Fetch the signed-in investor's own dashboard metrics. Returns
 * { data: metrics, error }. `metrics` is the aggregate object computed
 * server-side (analysisVolume, verdicts, conversion, pipelineByStatus,
 * hotspots, reviewNext, recentAnalyses, trialUsage).
 */
export async function fetchDashboardMetrics() {
  if (!supabase) return NOT_CONFIGURED;

  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) return NOT_SIGNED_IN;

  try {
    const response = await fetch(`${API_BASE_URL}/get-dashboard-metrics`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false) {
      return {
        data: null,
        error: {
          message: payload?.error || `Request failed (HTTP ${response.status}).`,
          status: response.status,
        },
      };
    }
    return { data: payload.metrics, error: null };
  } catch (err) {
    return { data: null, error: { message: err?.message || 'Network error.' } };
  }
}
